import { useCallback, useMemo, useState } from 'react';

import { rowSlugs } from '../slugs';
import {
  EMPTY_SPECIES_BASELINE,
  acknowledgeSpecies,
  balanceIdentifiers,
  captureSpeciesBaseline,
  realignBaselineRows,
  removeBaselineRow,
  speciesChanges,
} from '../speciesChanges';

const sameSlugs = (a, b) => a.length === b.length && a.every((slug, idx) => slug === b[idx]);

/**
 * Flags inputs / outputs whose species changed after the balance started
 * using them. See speciesChanges.js for how the baseline is kept.
 *
 * Callers must report row removals via `removeRow`, lists rewritten
 * wholesale (by the AI assistant) via `realign`, and whole-form reseeds
 * (Clear, Load example) via `reset`, since the baseline is positional.
 */
export const useSpeciesBaseline = ({ inputs, outputs, inputSlugs, outputSlugs, balance }) => {
  const [baseline, setBaseline] = useState(EMPTY_SPECIES_BASELINE);
  // Rewrites `realign` has heard about whose rows haven't reached the form
  // yet: `{ kind, fromSlugs, toSlugs }`.
  const [rewrites, setRewrites] = useState([]);

  const rows = useMemo(
    () => ({
      inputs: { species: (inputs ?? []).map(io => io?.species), slugs: inputSlugs },
      outputs: { species: (outputs ?? []).map(io => io?.species), slugs: outputSlugs },
    }),
    [inputs, outputs, inputSlugs, outputSlugs],
  );
  const referenced = useMemo(() => balanceIdentifiers(balance), [balance]);

  // Adjusting state during render rather than in an effect: the capture is
  // derived from props, and `captureSpeciesBaseline` returns the same object
  // when there's nothing new, so this settles after one extra render. A
  // rewrite is applied in the render its rows arrive in: applied earlier, the
  // capture would still see the old rows and fold it back onto them.
  const landed = rewrites.filter(r => sameSlugs(rows[r.kind].slugs, r.toSlugs));
  const realigned = landed.reduce((b, r) => realignBaselineRows(b, r.kind, r.fromSlugs, r.toSlugs), baseline);
  const captured = captureSpeciesBaseline(realigned, rows, referenced);
  if (landed.length) setRewrites(rewrites.filter(r => !landed.includes(r)));
  if (captured !== baseline) setBaseline(captured);

  const changes = useMemo(() => speciesChanges(captured, rows), [captured, rows]);

  const acknowledge = useCallback(({ kind, idx, to }) => setBaseline(b => acknowledgeSpecies(b, kind, idx, to)), []);
  const removeRow = useCallback((kind, idx) => setBaseline(b => removeBaselineRow(b, kind, idx)), []);
  // A second rewrite of a list before the first one lands still maps from
  // the rows the baseline is aligned with.
  const realign = useCallback(
    (kind, previousRows, nextRows) =>
      setRewrites(pending => {
        const earlier = pending.find(r => r.kind === kind);
        const fromSlugs = earlier?.fromSlugs ?? rowSlugs(previousRows);
        return [...pending.filter(r => r !== earlier), { kind, fromSlugs, toSlugs: rowSlugs(nextRows) }];
      }),
    [],
  );
  const reset = useCallback(() => {
    setBaseline(EMPTY_SPECIES_BASELINE);
    setRewrites([]);
  }, []);

  return { changes, acknowledge, removeRow, realign, reset };
};
